import React, { useState } from "react";
import { ArrowRight, Bell, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import PageShell from "@/components/layout/PageShell";

const previews = [
  {
    src: "/app-preview/1000038532.webp",
    alt: "APOS Companion state-matched plan with breathing, movement, and recovery guidance",
    title: "State-matched actions",
  },
  {
    src: "/app-preview/1000038533.webp",
    alt: "APOS Companion adaptive score and response history screen",
    title: "Progress and response history",
  },
  {
    src: "/app-preview/1000038592.webp",
    alt: "APOS Companion nervous-system check-in screen",
    title: "Current-state check-ins",
  },
];

const CompanionComingSoon = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    if (!email || status === "submitting") return;
    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch("/api/companion-waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source: "companion-coming-soon",
          website,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok && response.status !== 409) {
        throw new Error(body.error || "We could not save your email. Please try again.");
      }
      setStatus("success");
      setMessage(body.message || "Your place is saved. We will email you when Companion access opens.");
    } catch (error) {
      setStatus("error");
      setMessage(error.message);
    }
  };

  return (
    <PageShell forceSiteChrome>
      <section className="relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-28 sm:pt-24">
        <div className="pointer-events-none absolute -top-64 left-1/2 h-[900px] w-[900px] -translate-x-1/2 forr-radial-teal opacity-60" />
        <div className="relative mx-auto max-w-[1180px]">
          <div className="mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-3">
              <span className="forr-hairline" />
              <span className="forr-overline">APOS Companion · Coming soon</span>
              <span className="forr-hairline" />
            </div>
            <h1 className="mt-7 font-display text-5xl font-medium leading-[1.02] tracking-[-0.04em] forr-text-ink sm:text-7xl">
              Turn your assessment into daily nervous-system guidance.
            </h1>
            <p className="mx-auto mt-7 max-w-3xl text-lg font-light leading-relaxed forr-text-soft sm:text-xl">
              APOS Companion will connect your saved Adaptive Profile to current-state check-ins, matched physiological actions, planning, progress, learning, and Companion AI.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed forr-text-mute sm:text-base">
              The app is in beta while subscription and payment access are prepared. The Adaptive Assessment and personalized results are available now.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a href="#early-access" className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold forr-btn-primary">
                <Bell size={17} /> Save my place
              </a>
              <Link to="/assessment/start" className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold forr-btn-secondary">
                Take the assessment <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="mt-16 grid items-start gap-5 sm:mt-20 md:grid-cols-3">
            {previews.map((preview, index) => (
              <figure key={preview.src} className={`overflow-hidden rounded-[28px] border bg-white p-3 ${index === 1 ? "md:mt-10" : ""}`} style={{ borderColor: "var(--forr-line)", boxShadow: "var(--forr-shadow-lg)" }}>
                <img src={preview.src} alt={preview.alt} className="aspect-[9/16] w-full rounded-[20px] object-cover object-top" loading={index === 0 ? "eager" : "lazy"} />
                <figcaption className="px-3 pb-2 pt-4 text-sm font-medium forr-text-ink">{preview.title}</figcaption>
              </figure>
            ))}
          </div>

          <section id="early-access" className="mx-auto mt-16 max-w-3xl scroll-mt-28 rounded-[30px] p-7 forr-surface sm:mt-24 sm:p-11" style={{ border: "1px solid var(--forr-line)", boxShadow: "var(--forr-shadow-md)" }}>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-tint"><Mail size={22} className="forr-text-teal-deep" /></div>
            <div className="mt-6 forr-overline">Early access</div>
            <h2 className="mt-2 font-display text-3xl font-medium tracking-[-0.02em] forr-text-ink sm:text-4xl">Save your place for APOS Companion.</h2>
            <p className="mt-4 text-base leading-relaxed forr-text-soft">Leave your email and we will notify you when subscriptions and new Companion access open.</p>

            {status === "success" ? (
              <div className="mt-7 flex items-start gap-3 rounded-2xl p-5 forr-panel-action" role="status">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 forr-text-teal-deep" />
                <p className="text-sm leading-relaxed forr-text-ink">{message}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7">
                <label htmlFor="companion-waitlist-email" className="text-sm font-medium forr-text-ink">Email address</label>
                <input tabIndex="-1" aria-hidden="true" autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} className="absolute left-[-9999px] h-px w-px overflow-hidden" name="website" />
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <input id="companion-waitlist-email" required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" className="min-h-12 flex-1 rounded-full border bg-white px-5 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/30" style={{ borderColor: "var(--forr-line-strong)" }} />
                  <button type="submit" disabled={status === "submitting"} className="min-h-12 rounded-full px-7 text-sm font-semibold forr-btn-primary disabled:opacity-50">
                    {status === "submitting" ? "Saving…" : "Save my place"}
                  </button>
                </div>
                {status === "error" && <p className="mt-3 text-sm text-red-700" role="alert">{message}</p>}
              </form>
            )}

            <div className="mt-6 flex items-start gap-2 text-xs leading-relaxed forr-text-mute">
              <ShieldCheck size={15} className="mt-0.5 shrink-0" />
              <span>By saving your place, you agree to receive APOS Companion availability and early-access emails. You can unsubscribe or request deletion at any time by emailing <a href="mailto:forrhealth.0@gmail.com" className="font-semibold forr-text-teal-deep">forrhealth.0@gmail.com</a>.</span>
            </div>
            <p className="mt-5 text-xs forr-text-mute">Current beta tester? <Link to="/app?beta=1" className="font-semibold forr-text-teal-deep">Sign in to the beta</Link>.</p>
          </section>
        </div>
      </section>
    </PageShell>
  );
};

export default CompanionComingSoon;
